import React, { useEffect, useState, useMemo } from "react";
import {
	GetMessageContent,
	IsMessageSelected,
	useBatchMessageScheduleConfiguration,
	ValidateMessageScheduleRecord,
} from "../../../hooks/useBatchMessageScheduleConfiguration";
import { LanguageModel } from "../../../models/BatchMessage/BatchMessageLanguageDetailModel";
import { Card, CardContent, Tooltip } from "../../../common/MaterialComponent";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import "./MessageScheduleConfiguration.css";
import BatchMessageDetailModel from "../../../models/BatchMessage/BatchMessageDetailModel";
import { ILanguage } from "../../../common/interfaces";
import { MessageScheduleRow } from "../MessageScheduleRow/MessageScheduleRow";
import BatchMessageCostRequestModel, {
	Group,
	MessageCost,
} from "../../../models/BatchMessage/BatchMessageCostRequestModel";
import BatchMessageSchedulingDetailModel, {
	MessageDetail,
	RecurrenceDetail,
} from "../../../models/BatchMessage/BatchMessageSchedulingDetailModel";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	CircularProgress,
	Grid,
	Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { GroupDetail } from "../../../models/BatchMessage/BatchMessageGroupDetailModel";
import Shared from "../../../common/shared";
import { ButtonPrimary, LinkPrimary } from "../../../common/sharedStyle";
import { BatchMessageTypeEnum } from "../../../common/enum";
import { GetMessageScheduleCostDetail } from "../../../api/batchNotificationController";
import GraphemeSplitter from "grapheme-splitter";
import { SegmentedMessage } from "../../../common/messageSegmentCalculation";
import Alert from "@material-ui/lab/Alert";

export interface MessageScheduleConfigurationProps {
	batchNotification: BatchMessageDetailModel;
	onBatchNotificationChange: React.Dispatch<
		React.SetStateAction<BatchMessageDetailModel>
	>;
	isReadOnly: boolean;
	setHasStep4ConfigChanged: React.Dispatch<React.SetStateAction<boolean>>;
	highlightRequiredDetail: boolean;
	isNextButtonClick: boolean;
	resetNextButtonClick: (clicked: boolean) => void;
}

export const MessageScheduleConfiguration: React.FC<MessageScheduleConfigurationProps> = ({
	batchNotification,
	onBatchNotificationChange,
	isReadOnly,
	setHasStep4ConfigChanged,
	highlightRequiredDetail,
	isNextButtonClick,
	resetNextButtonClick,
}) => {
	const strings = useGetComponentStringsContext().MessageScheduleConfiguration;
	// use reducer for all state logic
	const [state, dispatch] = useBatchMessageScheduleConfiguration();
	const [isLoadingMessageCost, setIsLoadingMessageCost] = useState<boolean>(
		false
	);
	const [isExpanded, setIsExpanded] = useState<boolean | number>(false);
	const [isValidOnCalculateCost, setIsValidOnCalculateCost] = useState<boolean>(
		false
	);
	const [
		MessageScheduleCostRetrieved,
		setMessageScheduleCostRetrieved,
	] = useState<Boolean>(false);

	useEffect(() => {
		dispatch({
			type: "INITIALIZE_BATCH_MESSAGE_SCHEDULE_DETAIL",
			payload: {
				batchNotification: batchNotification,
			},
		});
		// disabled eslint rule as useEffect needs to be called once on page load to initialize state
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const hasVariables = useMemo(() => {
		let hasVars = false;
		batchNotification.batchMessageLanguageDetail?.languages.forEach(
			(language) => {
				if (
					Shared.messageDynamicVariables(undefined, undefined, language.message)
				) {
					hasVars = true;
				}
			}
		);

		return hasVars;
	}, [batchNotification]);

	useEffect(() => {
		// get the error in panel index
		const getPanelIndex = () => {
			if (
				state.batchMessageSchedulingDetails &&
				state.batchMessageSchedulingDetails.length > 0
			) {
				for (
					let index = 0;
					index < state.batchMessageSchedulingDetails.length;
					index++
				) {
					if (
						!ValidateMessageScheduleRecord(
							state.batchMessageSchedulingDetails[index].messages
						)
					) {
						return index;
					}
				}
			}
			return -1;
		};
		// open panel when highlightRequiredDetail or isValidOnCalculateCost value updated
		if (
			(highlightRequiredDetail || isValidOnCalculateCost) &&
			isNextButtonClick
		) {
			const panelNumber = getPanelIndex();
			setIsExpanded(panelNumber);
			resetNextButtonClick(false);
		}
	}, [
		highlightRequiredDetail,
		isNextButtonClick,
		resetNextButtonClick,
		state.batchMessageSchedulingDetails,
		isValidOnCalculateCost,
	]);

	useEffect(() => {
		const getSelectedMessageList = (groupIndex: number) => {
			const messageSchedulingDetailTemp = JSON.parse(
				JSON.stringify(state!.batchMessageSchedulingDetails[groupIndex])
			);
			if (
				messageSchedulingDetailTemp &&
				messageSchedulingDetailTemp.messages.length > 0
			) {
				return messageSchedulingDetailTemp.messages.filter(
					(x: MessageDetail) => x.isSelected
				);
			}
			return [];
		};
		const getMessageScheduleDetail = () => {
			const messageScheduleDetailList: BatchMessageSchedulingDetailModel[] = JSON.parse(
				JSON.stringify(state.batchMessageSchedulingDetails)
			);
			const batchMessageSchedulingDetails: BatchMessageSchedulingDetailModel = new BatchMessageSchedulingDetailModel();

			if (messageScheduleDetailList && messageScheduleDetailList.length > 0) {
				messageScheduleDetailList.forEach(
					(
						messageScheduleDetail: BatchMessageSchedulingDetailModel,
						groupIndex: number
					) => {
						const getSelectedMessageListItem = getSelectedMessageList(
							groupIndex
						).map((messageDetail: MessageDetail) => {
							messageDetail.dateTime = Shared.getFormattedDate(
								messageDetail.dateTime
							);
							return messageDetail;
						});
						if (
							getSelectedMessageListItem &&
							getSelectedMessageListItem.length > 0
						) {
							messageScheduleDetail.messages = getSelectedMessageListItem;
						} else {
							messageScheduleDetail.messages = [];
						}
						if (
							messageScheduleDetail &&
							messageScheduleDetail.messages.length > 0
						) {
							batchMessageSchedulingDetails.messages.push(
								...messageScheduleDetail.messages
							);
						}
					}
				);
				return batchMessageSchedulingDetails;
			}
			return null;
		};
		onBatchNotificationChange(
			(prevBatchNotificationDetail: BatchMessageDetailModel) => {
				return {
					...prevBatchNotificationDetail,
					batchMessageSchedulingDetail: getMessageScheduleDetail()
						? getMessageScheduleDetail()
						: null,
				};
			}
		);
	}, [onBatchNotificationChange, state]);

	const getIsBatchMessageTypeWhatsApp = useMemo(() => {
		return batchNotification.batchMessageType === BatchMessageTypeEnum.WhatsApp;
	}, [batchNotification.batchMessageType]);

	const onPanelClick = (panelNumber: number) => {
		setIsExpanded((prevPanel) =>
			prevPanel !== panelNumber ? panelNumber : false
		);
	};

	const getGroupName = (groupId: string) => {
		if (
			batchNotification &&
			batchNotification.batchMessageGroupDetail &&
			batchNotification.batchMessageGroupDetail.groupDetails
		) {
			const groupDetailRecord = batchNotification.batchMessageGroupDetail?.groupDetails.find(
				(x: GroupDetail) => x.id === groupId
			);
			return groupDetailRecord ? groupDetailRecord.name : "";
		}
		return "";
	};

	const getGroupMessageCost = (groupId: string, groupIndex: number) => {
		let groupCost: number = 0;
		if (batchNotification && batchNotification.totalCost > 0) {
			const messages = state.batchMessageSchedulingDetails[groupIndex].messages;
			groupCost = messages.reduce((a, b) => a + b.cost, 0);
		} else {
			groupCost = getMessageCost(groupId);
		}
		return Shared.getFormatedCurrencyValue(
			groupCost,
			batchNotification.currency
		);
	};

	const getMessageCost = (groupId: string, messageTitle?: string) => {
		let cost: number | undefined = 0;
		const groupIndex = state.batchMessageCostDetail.groups.findIndex(
			(x: Group) => x.id === groupId
		);
		if (groupIndex !== -1) {
			if (messageTitle !== undefined) {
				cost = state.batchMessageCostDetail.groups[
					groupIndex
				].messageCosts.find((y: MessageCost) => y.title === messageTitle)?.cost;
			} else {
				cost = state.batchMessageCostDetail.groups[groupIndex].costPerGroup;
			}
		}
		return cost !== undefined ? cost : 0;
	};

	const getLanguages = (): string[] => {
		if (
			batchNotification &&
			batchNotification.batchMessageLanguageDetail &&
			batchNotification.batchMessageLanguageDetail.languages.length > 0
		) {
			const messageDetail: LanguageModel[] =
				batchNotification.batchMessageLanguageDetail.languages;
			if (messageDetail.length > 0) {
				return messageDetail
					.map((x: LanguageModel) => x.code)
					.filter(
						(value: any, index: number, self: any) =>
							self.indexOf(value) === index
					);
			}
		}
		return [];
	};

	const getTotalGroupMessage = (groupId: string) => {
		if (
			batchNotification &&
			batchNotification.batchMessageGroupDetail &&
			batchNotification.batchMessageGroupDetail.groupDetails
		) {
			const groupDetailRecord = batchNotification.batchMessageGroupDetail.groupDetails.find(
				(x: GroupDetail) => x.id === groupId
			);
			return groupDetailRecord ? groupDetailRecord.phoneNumberCount : 0;
		}
		return 0;
	};

	const selectedLanguageList = (): ILanguage[] => {
		return Shared.getLanguageByCode(getLanguages());
	};

	const getIsMessageSelected = () => {
		if (
			batchNotification.batchMessageSchedulingDetail &&
			batchNotification.batchMessageSchedulingDetail.messages.length > 0
		) {
			return IsMessageSelected(
				batchNotification.batchMessageSchedulingDetail.messages
			);
		}
		return false;
	};

	const getIsBatchMessageTypeSMS = () => {
		return batchNotification &&
			batchNotification.batchMessageType === BatchMessageTypeEnum.SMS
			? true
			: false;
	};

	const getMessageContentLength = (
		groupIndex: number,
		messageIndex: number
	) => {
		const messageContent = GetMessageContent(
			state.batchMessageSchedulingDetails,
			batchNotification!.batchMessageLanguageDetail!.languages,
			getIsBatchMessageTypeSMS(),
			messageIndex,
			groupIndex
		);
		if (getIsBatchMessageTypeSMS()) {
			const graphemeSplitter = new GraphemeSplitter();
			const segmentedMessage = new SegmentedMessage(
				messageContent,
				"auto",
				graphemeSplitter
			);
			return segmentedMessage.segments.length;
		} else {
			return messageContent;
		}
	};

	const calculateTotalGroupCost = async () => {
		if (
			!ValidateMessageScheduleRecord(
				batchNotification.batchMessageSchedulingDetail
					? batchNotification.batchMessageSchedulingDetail.messages
					: []
			)
		) {
			//missing a field so highlight required field in child
			setIsValidOnCalculateCost(true);
			resetNextButtonClick(true);
		} else {
			// calculate total group cost
			const messageCostRequestModel: BatchMessageCostRequestModel = new BatchMessageCostRequestModel();
			state.batchMessageSchedulingDetails.forEach(
				(batchMessageScheduleDetail, groupIndex) => {
					const group: Group = new Group();
					batchMessageScheduleDetail.messages.forEach(
						(messageDetail, messageIndex) => {
							if (messageDetail.isSelected) {
								const messageCost: MessageCost = new MessageCost();
								messageCost.id = messageDetail.id;
								messageCost.length = getMessageContentLength(
									groupIndex,
									messageIndex
								);
								messageCost.title = messageDetail.title;
								group.messageCosts.push(messageCost);
								group.id = messageDetail.groupId;
							}
						}
					);
					messageCostRequestModel.preSubmissionConfigurationId =
						batchNotification.id;
					messageCostRequestModel.partitionKey = batchNotification.partitionKey;
					if (group.id) {
						messageCostRequestModel.groups.push(group);
					}
				}
			);
			try {
				setIsLoadingMessageCost(true);
				const res = await GetMessageScheduleCostDetail(messageCostRequestModel);
				if (res.responseObj) {
					if (res.responseObj) {
						dispatch({
							type: "SET_MESSAGE_COST_DETAIL",
							payload: {
								messageCost: res.responseObj,
							},
						});
						setMessageScheduleCostRetrieved(true);
					}
				} else {
					Shared.showSnackbarMessage(strings.messageCostErrorMessage);
				}
				setIsLoadingMessageCost(false);
			} catch (err) {
				setIsLoadingMessageCost(false);
				Shared.showSnackbarMessage(strings.messageCostErrorMessage);
			}
		}
	};

	return (
		<>
			<div className="messageSchedule">
				{state.batchMessageSchedulingDetails &&
					state.batchMessageSchedulingDetails.length > 0 &&
					state.batchMessageSchedulingDetails.map(
						(group: BatchMessageSchedulingDetailModel, groupIndex: number) => (
							<Accordion
								elevation={1}
								expanded={isExpanded === groupIndex}
								onChange={() => onPanelClick(groupIndex)}
								key={groupIndex}
								data-testid="messageScheduleAccording"
							>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography className="groupName">
										{getGroupName(
											group.messages && group.messages.length > 0
												? group.messages[0].groupId
												: ""
										)}
									</Typography>
									{!getIsBatchMessageTypeWhatsApp ? (
										<Typography>
											<Tooltip title={strings.totalCostToolTip}>
												<span>
													{`${strings.totalGroupCostLabel} `}
													<b data-testid="groupCostId">
														{batchNotification?.africasTalkingEnabled
															? "N/A"
															: getGroupMessageCost(
																	group.messages && group.messages.length > 0
																		? group.messages[0].groupId
																		: "",
																	groupIndex
															  )}
													</b>
												</span>
											</Tooltip>
										</Typography>
									) : (
										<Typography data-testid="totalGroupMessage">
											{strings.totalGroupMessageLabel} &nbsp;
											<b>{getTotalGroupMessage(group.messages[0].groupId)}</b>
										</Typography>
									)}
								</AccordionSummary>
								<AccordionDetails>
									<Card variant="outlined">
										<CardContent className={"group" + groupIndex}>
											{group.messages.map(
												(
													messageDetail: MessageDetail,
													messageIndex: number
												) => (
													<MessageScheduleRow
														key={groupIndex + "" + messageIndex}
														groupIndex={groupIndex}
														groupId={messageDetail.groupId}
														messageIndex={messageIndex}
														messageDetail={messageDetail}
														selectedLanguageList={selectedLanguageList()}
														isReadOnly={isReadOnly}
														country={batchNotification.country}
														currency={batchNotification.currency}
														highlightRequiredDetail={
															messageDetail.isSelected
																? highlightRequiredDetail ||
																  isValidOnCalculateCost
																: false
														}
														getIsBatchMessageTypeSMS={getIsBatchMessageTypeSMS()}
														getIsBatchMessageTypeWhatsApp={
															getIsBatchMessageTypeWhatsApp
														}
														batchMessageCostDetail={
															state.batchMessageCostDetail
														}
														batchMessageLanguageDetail={
															batchNotification &&
															batchNotification.batchMessageLanguageDetail
														}
														batchMessageSchedulingDetails={
															state.batchMessageSchedulingDetails
														}
														africasTalkingEnabled={
															batchNotification?.africasTalkingEnabled
														}
														onMessageSelectionChange={(
															isChecked: boolean,
															messageIndex: number,
															groupIndex?: number
														) => {
															dispatch({
																type: "MESSAGE_SELECTION_CHANGE",
																payload: {
																	isChecked,
																	groupIndex,
																	messageIndex,
																},
															});
															setHasStep4ConfigChanged(true);
														}}
														onLanguageChangeHandler={(
															values: ILanguage[],
															messageIndex: number,
															groupIndex?: number
														) => {
															dispatch({
																type: "LANGUAGE_SELECTION_CHANGE",
																payload: {
																	values,
																	groupIndex,
																	messageIndex,
																	languages:
																		batchNotification &&
																		batchNotification.batchMessageLanguageDetail
																			?.languages,
																},
															});
															setHasStep4ConfigChanged(true);
														}}
														onDateTimeValueChange={(
															value: Date | string,
															messageIndex: number,
															groupIndex?: number
														) => {
															dispatch({
																type: "DATE_TIME_SELECTION_CHANGE",
																payload: { value, groupIndex, messageIndex },
															});
															setHasStep4ConfigChanged(true);
														}}
														onTimeZoneChange={(
															value: string,
															messageIndex: number,
															groupIndex?: number
														) => {
															dispatch({
																type: "TIME_ZONE_SELECTION_CHANGE",
																payload: { value, groupIndex, messageIndex },
															});
															setHasStep4ConfigChanged(true);
														}}
														onRecurrenceDetailChange={(
															isRecurring: boolean,
															messageIndex: number,
															recurrenceDetails: RecurrenceDetail | undefined,
															groupIndex?: number
														) => {
															dispatch({
																type: "RECURRENCE_DETAIL_CHANGE",
																payload: {
																	isRecurring,
																	messageIndex,
																	recurrenceDetails,
																	groupIndex,
																},
															});
															setHasStep4ConfigChanged(true);
														}}
													/>
												)
											)}
										</CardContent>
									</Card>
								</AccordionDetails>
							</Accordion>
						)
					)}
				{!getIsBatchMessageTypeWhatsApp ? (
					<div className="addBtnDiv">
						{hasVariables && (
							<div className="dynamicVariablesWarning">
								<Alert severity="info" variant="outlined">
									{strings.variablesPresent}
								</Alert>
							</div>
						)}
						<div className="calculateCostBtnContainer">
							<ButtonPrimary
								variant="contained"
								onClick={calculateTotalGroupCost}
								color="primary"
								disabled={
									!getIsMessageSelected() ||
									isReadOnly ||
									batchNotification?.africasTalkingEnabled
								}
								data-testid="calculateCostBtn"
								className={
									"textAlignRight " +
									(isLoadingMessageCost || !getIsMessageSelected()
										? "disabledBtn"
										: " ")
								}
							>
								{strings.calculateCostButton}
							</ButtonPrimary>
						</div>
						<span className="btnMessageTotalSpan">
							{isLoadingMessageCost && (
								<CircularProgress
									size={24}
									className="btnLoader"
									color="primary"
								/>
							)}
						</span>
						<Tooltip
							title={strings.approximateTotalCostToolTip}
							className="totalCost"
						>
							<span>
								{`${strings.totalCostText} `}
								<b data-testid="totalCostId">
									{batchNotification?.africasTalkingEnabled
										? "N/A"
										: Shared.getFormatedCurrencyValue(
												batchNotification && batchNotification.totalCost
													? batchNotification.totalCost
													: state.batchMessageCostDetail.totalCost,
												batchNotification.currency
										  )}
								</b>
							</span>
						</Tooltip>
						{Shared.hasRecurringMessages(batchNotification) &&
							MessageScheduleCostRetrieved && (
								<div className="recurringCostWarning">
									<Alert severity="info" variant="outlined">
										<b>{strings.approximateTotalCostRecurringWarning}</b>
										<br />
										{strings.variableCostChangeWarning}
									</Alert>
								</div>
							)}
					</div>
				) : (
					!isReadOnly && (
						<Grid item xs={12} data-testid="infoMessage">
							<div className="infoMessage">
								<span>{strings.calculateApproximateMessageCostText} </span>
								<LinkPrimary
									href="https://www.twilio.com/whatsapp/pricing"
									target="blank"
								>
									https://www.twilio.com/whatsapp/pricing
								</LinkPrimary>
							</div>
						</Grid>
					)
				)}
			</div>
		</>
	);
};
